import { useField } from 'formik';
import React from 'react';
import Text from '@service-victoria/component-library/components/Text';
import Input from '@service-victoria/component-library/components/Input';
import {
  isReturnExemption,
  isVictorianReturningFromRedZone,
  isFullVaxERZVictorian
} from 'steps/Assess/travel-reasons';
import { showTransitInvalidWarning } from 'steps/Assess/selectors';
import * as Interfaces from 'interfaces';
import { Address } from 'Components/Address';
import * as Styled from '../../../styled';
import { Props as AutoCompleteProps } from '@service-victoria/component-library/components/AutoComplete';
import type { DeepPartial } from 'types/globals';

type HomeAddressProps = {
  setDeltaValues: (values: DeepPartial<Pick<Interfaces.Common.FormValues, 'assess'>>) => void;
};

export const DestinationAddress: React.FC<HomeAddressProps> = ({ setDeltaValues }) => {
  const [{ value: reasonCode }] = useField<string>('assess.reasons');
  const [{ value: returningVicInvalidDestination }] = useField<boolean>(
    'assess.Depart.returningVicInvalidDestination'
  );
  const [{ value: targetState }] = useField<string>('assess.Depart.target.fullDetails.state');

  const onSelection = (
    address: Interfaces.Common.AddressWithLga,
    selectedValue: AutoCompleteProps.OptionType
  ) => {
    setDeltaValues({
      assess: {
        Depart: {
          target: {
            fullDetails: {
              id: address.id,
              suburb: address.suburbOrPlaceOrLocality,
              country: address.country,
              lga: address.lga,
              state: address.stateOrTerritory,
              postCode: address.postCode,
              fullAddress: address.addressLine1
            },
            summaryData: selectedValue
          },
          returningVicInvalidDestination:
            (isVictorianReturningFromRedZone(reasonCode) ||
              isReturnExemption(reasonCode) ||
              isFullVaxERZVictorian(reasonCode)) &&
            address.stateOrTerritory !== 'VIC'
        }
      }
    });
  };

  return (
    <>
      <Styled.Spacer24 />
      <Text.Heading.H3 fontWeight='bold'>Main destination</Text.Heading.H3>
      <Styled.Spacer24 />
      <Input
        label='Place or name of person'
        name='assess.Depart.targetPlace'
        data-testid='TargetPlace-Input'
        errorLabelProps={{
          'data-testid': 'TargetPlace-InputError'
        }}
      />
      <Styled.Spacer24 />
      <Address
        fieldName='assess.Depart.target'
        label='Destination address'
        onSelection={onSelection}
        addressType='both'
      />
      <Styled.Spacer24 />
      {returningVicInvalidDestination && (
        <Styled.NotificationInfo
          value='To apply for this permit type, you must be a Victorian resident. Your destination address must be in Victoria.'
          data-testid='VictorianResident-Notification'
        />
      )}
      {showTransitInvalidWarning(reasonCode, targetState) && (
        <Styled.NotificationInfo
          value='This address is in Victoria. Your main destination must not be in Victoria.'
          aria-invalid='true'
          aria-labelledby='assess.Depart.target.summaryData.label'
          data-testid='TransitDestinationInvalid-Notification'
        />
      )}
    </>
  );
};

export default DestinationAddress;
